import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Code, Layers, ShieldCheck, Search, Sparkles, ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "./LanguageContext";

// Beautiful, premium, and silky-smooth fade-and-slide transition for the target industry sectors
const ElegantWordTransition: React.FC<{ text: string }> = ({ text }) => {
  return (
    <span className="relative inline-block min-h-[1.25em] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span
          key={text}
          initial={{ opacity: 0, y: 15, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -15, filter: "blur(4px)" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="inline-block bg-gradient-to-r from-indigo-700 via-indigo-600 to-indigo-500 bg-clip-text text-transparent select-none"
        >
          {text}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export default function Services() {
  const { t } = useLanguage();

  const words = t("alternatingKeywords", "services") || [
    "Healthcare SaaS",
    "HR SaaS",
    "Construction SaaS",
    "Logistics SaaS"
  ];
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    setWordIdx(0); // Reset index on language change to prevent out of bounds
  }, [words.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIdx((prev) => (prev + 1) % words.length);
    }, 3200);
    return () => clearInterval(timer);
  }, [words.length]);

  const servicesList = [
    {
      icon: Code,
      tag: t("b1Tag", "services") || "SaaS & App Development",
      title: t("b1Title", "services") || "Full-Stack Software Engineering",
      desc: t("b1Desc", "services") || "Tailor-made web apps, robust SaaS platforms, and native mobile applications engineered with perfect type-safety, high-performance runtime logic, and modular, scalable backends.",
      color: "text-indigo-600 bg-indigo-55/60 border-indigo-100",
      delay: 0.1
    },
    {
      icon: Layers,
      tag: t("b2Tag", "services") || "Design Systems & UI",
      title: t("b2Title", "services") || "Tactile UX & Motion Interfaces",
      desc: t("b2Desc", "services") || "Interactive vector curves, high-contrast premium aesthetics, and sensory transitions built with motion/react, engineered to grab user attention while simplifying workflow systems.",
      color: "text-purple-600 bg-purple-55/60 border-purple-100",
      delay: 0.2
    },
    {
      icon: ShieldCheck,
      tag: t("b3Tag", "services") || "Cloud & Security Architecture",
      title: t("b3Title", "services") || "Secure Database & Cloud Scaling",
      desc: t("b3Desc", "services") || "Robust relational table structures, customized cloud-native instances, and secure firewalls with cryptographic validation protecting your software core dynamically.",
      color: "text-emerald-600 bg-emerald-55/60 border-emerald-100",
      delay: 0.3
    },
    {
      icon: Search,
      tag: t("b4Tag", "services") || "Enterprise Strategy & SEO",
      title: t("b4Title", "services") || "World-Class SEO & Performance",
      desc: t("b4Desc", "services") || "Crawl-ready JSON-LD Schema markups, automated breadcrumbs, meta synchronization, and native multilingual routing optimized for elite visibility and page load speeds.",
      color: "text-[#eab308] bg-yellow-55/60 border-yellow-100",
      delay: 0.4
    }
  ];

  return (
    <section id="services" className="relative py-24 md:py-36 bg-white overflow-hidden border-b border-gray-100 text-left">
      {/* Cinematic Grid Lines Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.012)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.009)_1px,transparent_1px)] bg-[size:6rem_6rem] pointer-events-none opacity-40" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Section Header Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-20">
          <div className="lg:col-span-7 flex flex-col gap-4">
            {/* Custom Luxury Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-50 border border-gray-150/40 w-fit text-[10px] font-mono tracking-widest text-[#4f46e5] uppercase"
            >
              <Sparkles size={11} className="animate-pulse" /> {t("tag", "services") || "Custom Product Studio / We Build, Scale & Maintain SaaS Platforms"}
            </motion.div>

            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#111827] mt-2 leading-[1.2] flex flex-wrap items-center gap-x-2">
              <span>{t("dedicatedPartnerHeader", "services") || "Your Dedicated Partner for"}</span>
              <ElegantWordTransition text={words[wordIdx]} />
            </h2>
          </div>

          <div className="lg:col-span-5 text-left">
            <p className="text-gray-500 font-sans text-base leading-relaxed font-light">
              {t("desc", "services") || "Teralumax operates as an elite Software Product Studio. We build, scale, and maintain tailor-made SaaS platforms and custom digital products from architectural blueprint to global scale, helping potential clients secure reliable, long-term technical execution and product success."}
            </p>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {servicesList.map((srv, idx) => {
            const IconComponent = srv.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: srv.delay, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className="group relative flex flex-col justify-between p-8 sm:p-10 rounded-3xl bg-slate-50/50 border border-gray-150/40 hover:bg-white hover:border-[#4f46e5]/25 hover:shadow-[0_20px_50px_rgba(79,70,229,0.06)] transition-all duration-300 overflow-hidden"
              >
                {/* Accent Background Glow */}
                <div className="absolute right-0 bottom-0 w-32 h-32 bg-radial from-indigo-50/5 to-transparent pointer-events-none group-hover:from-indigo-100/10 group-hover:scale-125 transition-transform duration-500" />
                
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <div className={`p-4 rounded-2xl border ${srv.color} transition-colors group-hover:bg-[#4f46e5]/5 group-hover:text-[#4f46e5] group-hover:border-[#4f46e5]/10`}>
                      <IconComponent size={22} className="group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <span className="text-[9px] font-mono tracking-widest text-slate-400 uppercase font-semibold group-hover:text-indigo-500 transition-colors">
                      {srv.tag}
                    </span>
                  </div>

                  <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-[#111827] mb-4 tracking-tight">
                    {srv.title}
                  </h3>

                  <p className="text-gray-500 font-sans text-sm leading-relaxed font-light mb-8 group-hover:text-gray-600 transition-colors">
                    {srv.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100/60 flex items-center justify-between">
                  {/* Subtle Action Link */}
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-[10px] font-mono tracking-widest text-[#4f46e5] group-hover:text-brand-charcoal font-semibold transition-colors"
                  >
                    <span>START PROJECT HANDSHAKE</span>
                    <ArrowRight size={12} className="transform group-hover:translate-x-1 transition-transform" />
                  </a>
                  <span className="text-[11px] font-mono text-indigo-50 opacity-0 group-hover:opacity-100 select-none transition-opacity pointer-events-none font-bold">
                    [ SERVICE_NODE_0{idx + 1} ]
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* World-Class Client Assurance Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 p-8 sm:p-10 rounded-3xl bg-slate-900 text-white relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-8 border border-slate-800"
        >
          <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-radial from-indigo-500/10 to-transparent pointer-events-none" />
          
          <div className="flex-1">
            <span className="text-[9px] font-mono tracking-widest text-indigo-400 uppercase mb-2 block font-semibold">Client Trust Assurance</span>
            <h4 className="font-display font-medium text-lg sm:text-xl text-white mb-2 leading-snug">
              {t("guaranteeTitle", "services") || "Ready to build a custom platform with Teralumax?"}
            </h4>
            <p className="text-slate-400 font-sans font-light text-xs sm:text-sm leading-relaxed max-w-2xl">
              {t("guaranteeDesc", "services") || "Each collaboration operates under full-stack performance guarantees: subsecond page loading speeds, high-fidelity responsive design layout files, robust cloud infrastructure setup, and premium metadata indexing."}
            </p>
          </div>

          <a
            href="#contact"
            className="group shrink-0 inline-flex items-center gap-2 text-[10px] font-mono tracking-widest bg-white hover:bg-[#4f46e5] text-slate-900 hover:text-white px-7 py-4 rounded-full transition-all duration-300 font-bold focus:outline-hidden"
          >
            <span>INQUIRE NOW</span>
            <ArrowRight size={13} className="transform group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

      </div>
    </section>
  );
}
